/** Shared copy + HTML for the prerendered SEO pages. Keep this file
 *  CommonJS so the post-build writer and Jest can require() it. */

const SITE = 'https://playchipless.com';

const FAQS = [
  {
    q: 'What is Chipless?',
    a: "Chipless is a free web app that lets you play authentic poker with just a deck of cards. Instead of chips or end-of-night math, it tracks stacks, blinds, and bets, then shows who owes who when the game ends.",
  },
  {
    q: 'How do you play poker without chips?',
    a: 'Deal cards like normal and use the app for everything else. Each player joins from their phone, one host runs the table, and the app handles the stacks and blinds. At the end it instantly shows who owes who.',
  },
  {
    q: 'Why use Chipless instead of online poker?',
    a: 'You still use real cards, sit at a real table, and play face-to-face with friends. Only the chips and payouts go digital — so you get authentic poker nights without owning a chip set.',
  },
  {
    q: 'Do I need to download anything?',
    a: 'No. Chipless is 100% web-based. Go to playchipless.com, host a game, and share the join code. Everyone plays from their phone — no downloads or sign-ups.',
  },
  {
    q: 'Who is Chipless for?',
    a: 'Friends who want to play without buying a chip set, travelers with only a deck of cards, beginners who want a simple way to run games, and anyone who loves poker but hates chips and manual math.',
  },
  {
    q: 'Does Chipless replace poker?',
    a: "No. You still deal real cards, bluff, bet, and talk at the table. The app only replaces the chips and the boring part — tracking stacks and showing payouts. It's still your game.",
  },
  {
    q: 'How much does it cost?',
    a: 'Chipless is completely free. All you need is a deck of cards and your phones.',
  },
  {
    q: 'How do payouts work?',
    a: 'At the end of the game, Chipless shows exactly who owes who — no debates and no calculating net results by hand.',
  },
  {
    q: 'Can I play anywhere?',
    a: 'Yes. As long as you have a deck of cards and your friends have their phones, you can run a game anywhere — dorms, hotels, road trips, or your kitchen table.',
  },
  {
    q: 'How do I get started?',
    a: 'Grab a deck of cards, go to playchipless.com, host a game and invite your friends, then deal cards like normal.',
  },
];

const HOW_TO_STEPS = [
  'Host starts a game on their phone.',
  'Friends join by QR or code with just a name.',
  'Deal real cards and bet with your phones.',
];

const HOW_TO_HOOK = 'All you need is a deck of cards and your phones.';

const HOW_TO_BEFORE_STEPS = [
  'I was on my graduation trip with my college friends. We always played poker, but nobody brought chips, and none of us liked fully online poker. So I built Chipless.',
  "The phones become the chips. You still deal a real deck, shuffle, and stare each other down. Everyone's phone is their stack, and it keeps track of the bets, blinds and pot. At the end it shows who owes who.",
];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function formatPostDate(iso) {
  const [year, month, day] = iso.split('-').map(Number);
  return `${MONTHS[month - 1]} ${day}, ${year}`;
}

const POSTS = [
  {
    title: 'How to play poker without chips',
    href: '/how-to-play-poker-without-chips',
    author: 'Michael Jonna',
    date: '2026-10-06',
    excerpt:
      'On my graduation trip we had cards but no chips, so I made our phones the chips. 100% free. No app download, no account needed.',
  },
  ...[
    ['Digital Poker Chips', '/digital-poker-chips', 'Chipless gives you digital poker chips for real home games.'],
    ['Poker Chip App', '/poker-chip-app', 'Chipless is a poker chip app for playing in person.'],
    ['Poker Without Chips App', '/poker-without-chips-app', 'No chip set? Play anyway.'],
    ['Home Poker Chip Tracker', '/home-poker-chip-tracker', 'Chipless tracks every stack, bet and pot at your home game.'],
  ].map(([title, href, excerpt]) => ({ title, href, author: 'Michael Jonna', date: '2026-10-29', excerpt })),
];

const PAGES = {
  howTo: {
    id: 'howTo',
    path: '/how-to-play-poker-without-chips',
    file: 'how-to-play-poker-without-chips.html',
    title: 'How to Play Poker Without Chips | Chipless',
    description:
      'On my graduation trip we had cards but no chips, so I made our phones the chips. 100% free. No app download, no account needed.',
    canonical: `${SITE}/how-to-play-poker-without-chips`,
    author: 'Michael Jonna',
  },
  faq: {
    id: 'faq',
    path: '/faq',
    file: 'faq.html',
    title: 'FAQ – How to Play Poker Without Chips',
    description:
      'Learn how to play real poker without chips. Chipless lets you deal cards, track bets, and instantly see who owes who — all you need is a deck of cards.',
    canonical: `${SITE}/faq`,
  },
  about: {
    id: 'about',
    path: '/about',
    file: 'about.html',
    title: 'About Chipless – Play Real Poker Without Chips | Chipless',
    description:
      'Learn why Chipless was created to make authentic poker games possible anywhere with just a deck of cards. No chips, no calculators, no hassle.',
    canonical: `${SITE}/about`,
  },
  blog: {
    id: 'blog',
    path: '/blog',
    file: 'blog.html',
    title: 'Blog | Chipless',
    description:
      'Notes on playing poker without chips — real cards, phones as the stack.',
    canonical: `${SITE}/blog`,
  },
};

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function backLink(tone, compact, href) {
  const color =
    tone === 'slate'
      ? 'text-slate-400 hover:text-white'
      : 'text-mute hover:text-ink';
  const pad = compact ? 'p-1 -ml-1' : 'p-1.5 -ml-1.5';
  const size = compact ? 16 : 20;
  return (
    `<a href="${href || '/'}" class="${pad} ${color} transition-colors" aria-label="Back">` +
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6"/></svg>` +
    '</a>'
  );
}

const RELATED = [
  { href: '/digital-poker-chips', label: 'Digital poker chips' },
  { href: '/poker-chip-app', label: 'Poker chip app' },
  { href: '/poker-without-chips-app', label: 'Poker without chips app' },
  { href: '/home-poker-chip-tracker', label: 'Home poker chip tracker' },
  { href: '/how-to-play-poker-without-chips', label: 'How to play poker without chips' },
  { href: '/faq', label: 'FAQ' },
];

function relatedLinks(current) {
  const links = RELATED.filter((r) => r.href !== current)
    .map(
      (r) =>
        `<li><a href="${r.href}" class="text-[13px] text-mute hover:text-ink underline underline-offset-4 decoration-line hover:decoration-ink transition-colors">${escapeHtml(r.label)}</a></li>`
    )
    .join('');
  return (
    `<nav class="mt-10" aria-label="Related">` +
    `<h2 class="text-[13px] font-semibold text-ink">Related</h2>` +
    `<ul class="mt-2 flex flex-wrap gap-x-4 gap-y-2">${links}</ul>` +
    `</nav>`
  );
}

function interlinks(tone, current) {
  const items = [
    { href: '/blog', label: 'Blog' },
    { href: '/faq', label: 'FAQ' },
    { href: '/about', label: 'About' },
    { href: '/', label: 'Home' },
  ];
  const linkClass =
    tone === 'slate'
      ? 'text-[11px] text-slate-500 hover:text-slate-300 transition-colors'
      : 'text-[11px] text-mute/50 hover:text-mute transition-colors';
  const navClass =
    tone === 'slate'
      ? 'mt-10 pt-6 border-t border-slate-700 flex justify-center gap-6 mx-auto w-fit'
      : 'mt-10 pt-6 border-t border-line-2 flex justify-center gap-6 mx-auto w-fit';
  return (
    `<nav class="${navClass}" aria-label="More about Chipless">` +
    items
      .map((item) => {
        const currentClass = item.href === current ? ' text-ink' : '';
        const slateCurrent = item.href === current ? ' text-slate-300' : '';
        const extra = tone === 'slate' ? slateCurrent : currentClass;
        return `<a href="${item.href}" class="${linkClass}${extra}">${escapeHtml(item.label)}</a>`;
      })
      .join('') +
    '</nav>'
  );
}

function renderHowToBody() {
  const steps = HOW_TO_STEPS.map(
    (step, i) =>
      `<li class="flex items-start"><span class="text-mute tnum mr-3 flex-shrink-0">${i + 1}.</span><span>${escapeHtml(step)}</span></li>`
  ).join('');
  const story = HOW_TO_BEFORE_STEPS.map(
    (para) => `<p class="mt-2 sm:mt-4 text-[15px] text-ink-2 leading-[1.45] sm:leading-relaxed">${escapeHtml(para)}</p>`
  ).join('');
  return (
    `<div class="min-h-[100dvh] bg-cream text-ink flex flex-col">` +
    `<header class="flex items-center px-5 pt-2 pb-0 sm:px-8 sm:pt-7 sm:pb-2">${backLink('cream', true, '/blog')}</header>` +
    `<main class="flex-1 w-full max-w-2xl mx-auto px-6 sm:px-8 pt-2 sm:pt-8 pb-16">` +
    `<h1 class="text-[26px] sm:text-[40px] leading-[1.12] tracking-[-0.02em] font-semibold text-ink">How to play poker without chips</h1>` +
    `<p class="mt-1.5 sm:mt-2 text-[14px] sm:text-[15px] text-mute leading-snug">By Michael Jonna <span aria-hidden="true">·</span> <button type="button" id="how-to-contact" class="text-mute hover:text-ink underline underline-offset-4 decoration-line hover:decoration-ink transition-colors">Contact</button></p>` +
    `<p class="mt-2.5 sm:mt-6 text-[15px] text-ink-2 leading-[1.45] sm:leading-relaxed font-semibold">${escapeHtml(HOW_TO_HOOK)}</p>` +
    story +
    `<ol class="mt-2.5 sm:mt-8 space-y-1 sm:space-y-3 text-[15px] text-ink-2 leading-[1.45] sm:leading-relaxed">${steps}</ol>` +
    `<a href="/" class="mt-3 sm:mt-8 inline-flex items-center justify-center gap-2 rounded-full font-medium bg-ink text-white px-6 py-3 sm:py-4 text-base w-full max-w-sm">Start a game <span aria-hidden="true">→</span></a>` +
    `<p class="mt-2 text-[12px] text-mute text-center leading-snug">100% free. No app download, no account needed.</p>` +
    `<p class="mt-6 text-[15px] text-ink-2 leading-relaxed">Chipless keeps poker real: real cards, real people at the table. No cards, or friends who aren't there? I'm a fan of <a href="https://www.pokernow.club" rel="noopener" class="underline underline-offset-4 decoration-line hover:decoration-ink">PokerNow</a>!</p>` +
    relatedLinks(PAGES.howTo.path) +
    interlinks('cream', PAGES.howTo.path) +
    `</main></div>`
  );
}

const FAQ_LEAD = {
  q: 'Can you play poker without chips?',
  a: 'Yes. Deal a real deck and use your phones as the chips. Chipless is a free web app for in-person poker — one table, real cards, no chip set.',
};

const FAQ_SUBLINE = 'Answers to what people ask about playing poker with Chipless.';

function renderFaqBody() {
  const items = [FAQ_LEAD, ...FAQS].map(
    (item) =>
      `<div class="py-6">` +
      `<h2 class="text-[17px] font-semibold text-ink">${escapeHtml(item.q)}</h2>` +
      `<p class="mt-2 text-[15px] text-ink-2 leading-relaxed">${escapeHtml(item.a)}</p>` +
      `</div>`
  ).join('');
  return (
    `<div class="min-h-[100dvh] bg-cream text-ink flex flex-col">` +
    `<header class="flex items-center px-5 pt-5 pb-2 sm:px-8 sm:pt-7">${backLink('cream')}</header>` +
    `<main class="flex-1 w-full max-w-2xl mx-auto px-6 sm:px-8 pt-8 pb-16">` +
    `<h1 class="text-[32px] sm:text-[40px] leading-[1.1] tracking-[-0.02em] font-semibold text-ink">FAQ</h1>` +
    `<p class="mt-3 text-[15px] text-mute leading-relaxed">${escapeHtml(FAQ_SUBLINE)}</p>` +
    `<div class="mt-10 divide-y divide-line-2">${items}</div>` +
    relatedLinks('/faq') +
    interlinks('cream', '/faq') +
    `</main></div>`
  );
}

const ABOUT_SECTIONS = [
  {
    h: 'Why I Built Chipless',
    paragraphs: [
      'I had the idea for Chipless on a graduation trip in Alaska with my college friends. We wanted to play poker one night, but no one had chips. Our only options were clunky calculator apps or going online — which meant we were all glued to our phones instead of enjoying the trip together.',
      "It didn't feel like real poker anymore. I wanted a way to deal cards at a table, laugh with friends, and keep the game authentic — without worrying about chips or math.",
      'So with no coding experience, I started building what I first called Pocket Poker — eventually landing on the name Chipless.',
    ],
  },
  {
    h: 'What Chipless Is',
    paragraphs: [
      "Chipless is a simple tool that turns any deck of cards into a full poker set. It doesn't replace the real game — it just replaces the chips. You still deal physical cards, make reads at the table, and enjoy poker with friends. The app just takes care of the boring parts: blinds, stacks, and showing who owes who at the end.",
    ],
  },
  {
    h: 'The Vision',
    paragraphs: [
      'Poker nights should be easy. No carrying chips, no debating payouts, no losing focus on the game itself. My goal with Chipless is to make authentic poker games possible anywhere — dorm rooms, ski trips, living rooms, or travel nights — with nothing more than a deck of cards and your phone.',
    ],
  },
  {
    h: 'How Chipless Was Built',
    paragraphs: [
      'Chipless started as a side project with almost no coding background besides a 1.5 credit python class, which I got a B in. Using AI coding tools, the app was created from scratch. The process was as much about proving that technology can bring ideas to life as it was about poker itself.',
      'The result is a simple, lightweight tool built with accessibility in mind: designed so anyone, anywhere, can turn a deck of cards into a full poker set.',
    ],
    after: 'Questions? Email me: <span class="font-semibold text-ink">playchipless@outlook.com</span>',
  },
];

function renderAboutBody() {
  const sections = ABOUT_SECTIONS.map((section) => {
    const paras = section.paragraphs
      .map((para, i) => {
        const last = i === section.paragraphs.length - 1;
        return last
          ? `<p>${escapeHtml(para)}</p>`
          : `<p class="mb-4">${escapeHtml(para)}</p>`;
      })
      .join('');
    const after = section.after
      ? `<p class="mt-4">${section.after}</p>`
      : '';
    return (
      `<div class="py-6">` +
      `<h2 class="text-[17px] font-semibold text-ink">${escapeHtml(section.h)}</h2>` +
      `<div class="mt-2 text-[15px] text-ink-2 leading-relaxed">${paras}${after}</div>` +
      `</div>`
    );
  }).join('');
  return (
    `<div class="min-h-[100dvh] bg-cream text-ink flex flex-col">` +
    `<header class="flex items-center px-5 pt-5 pb-2 sm:px-8 sm:pt-7">${backLink('cream')}</header>` +
    `<main class="flex-1 w-full max-w-2xl mx-auto px-6 sm:px-8 pt-8 pb-16">` +
    `<h1 class="text-[32px] sm:text-[40px] leading-[1.1] tracking-[-0.02em] font-semibold text-ink">About</h1>` +
    `<div class="mt-10 divide-y divide-line-2">${sections}</div>` +
    interlinks('cream', '/about') +
    `</main></div>`
  );
}

function renderPostCard(post) {
  return (
    `<li>` +
    `<a href="${escapeHtml(post.href)}" class="flex items-center gap-3 w-full rounded-2xl border border-line bg-cream-2 px-4 py-4 hover:border-ink active:bg-line-2 transition-colors">` +
    `<span class="min-w-0 flex-1">` +
    `<span class="block text-[17px] font-semibold text-ink leading-snug">${escapeHtml(post.title)}</span>` +
    `<span class="mt-1 block text-[14px] text-ink-2 leading-snug truncate">${escapeHtml(post.excerpt)}</span>` +
    `<span class="mt-1.5 block text-[13px] text-mute">By ${escapeHtml(post.author)} <span aria-hidden="true">·</span> ${escapeHtml(formatPostDate(post.date))}</span>` +
    `</span>` +
    `<span class="flex-shrink-0 text-[18px] text-mute leading-none" aria-hidden="true">→</span>` +
    `</a>` +
    `</li>`
  );
}

function renderBlogBody(posts) {
  const cards = (posts || POSTS).map(renderPostCard).join('');
  return (
    `<div class="min-h-[100dvh] bg-cream text-ink flex flex-col">` +
    `<header class="flex items-center px-5 pt-5 pb-2 sm:px-8 sm:pt-7">${backLink('cream')}</header>` +
    `<main class="flex-1 w-full max-w-2xl mx-auto px-6 sm:px-8 pt-8 pb-16">` +
    `<h1 class="text-[32px] sm:text-[40px] leading-[1.1] tracking-[-0.02em] font-semibold text-ink">Blog</h1>` +
    `<ul class="mt-8 space-y-3">${cards}</ul>` +
    interlinks('cream', '/blog') +
    `</main></div>`
  );
}


const LANDING_LEAD = 'Every player gets their own chips on their own phone. No app, no account, no frills.';

const LANDING_STEPS = [
  'One person taps Start a game.',
  'Friends join with the QR code or room code.',
  'Deal the cards and bet on your phones.',
];

const LANDINGS = [
  {
    id: 'digitalPokerChips',
    path: '/digital-poker-chips',
    h1: 'Digital Poker Chips',
    body: 'Chipless gives you digital poker chips for real home games. You still deal a real deck of cards. Your phones keep the stacks, the bets and the pot.',
    faqs: [
      { q: 'Are digital poker chips free?', a: 'Yes, Chipless is free.' },
      { q: 'Do I need to download anything?', a: "No, it runs in your phone's browser." },
      { q: 'Do I still need cards?', a: 'Yes, you play with a real deck at the table.' },
    ],
  },
  {
    id: 'pokerChipApp',
    path: '/poker-chip-app',
    h1: 'Poker Chip App',
    body: 'Chipless is a poker chip app for playing in person. Bring a deck of cards, and your phones handle the chips.',
    faqs: [
      { q: 'How many players?', a: 'Up to 10.' },
      { q: 'Does everyone need an account?', a: 'No, you join with a code.' },
      { q: 'Does it work on iPhone and Android?', a: 'Yes, in any phone browser.' },
    ],
  },
  {
    id: 'pokerWithoutChipsApp',
    path: '/poker-without-chips-app',
    h1: 'Poker Without Chips App',
    body: 'No chip set? Play anyway. Chipless replaces the chips with your phones, so all you need is a deck of cards.',
    faqs: [
      { q: 'Can you play poker without chips?', a: 'Yes, use your phones as the chips.' },
      { q: 'Is it online poker?', a: 'No, everyone plays at the same table with real cards.' },
      { q: 'What does it cost?', a: 'Nothing.' },
    ],
  },
  {
    id: 'homePokerChipTracker',
    path: '/home-poker-chip-tracker',
    h1: 'Home Poker Chip Tracker',
    body: 'Chipless tracks every stack, bet and pot at your home game, so nobody counts chips or does math by hand.',
    faqs: [
      { q: 'Does it track who owes who?', a: 'Yes, at the end of the game.' },
      { q: 'Can the host fix a stack?', a: 'Yes. In Host tools, the host can Undo the last action or Move chips from one player to another to fix a mistake.' },
      { q: 'Is there a login?', a: 'No.' },
    ],
  },
];

LANDINGS.forEach((l) => {
  PAGES[l.id] = {
    id: l.id,
    path: l.path,
    file: `${l.path.slice(1)}.html`,
    title: l.h1,
    description: `${LANDING_LEAD} ${l.body}`,
    canonical: `${SITE}${l.path}`,
    landing: l,
  };
});

function renderLandingBody(l) {
  const steps = LANDING_STEPS.map(
    (step, i) =>
      `<li class="flex items-start"><span class="text-mute tnum mr-3 flex-shrink-0">${i + 1}.</span><span>${escapeHtml(step)}</span></li>`
  ).join('');
  const faqs = l.faqs.map(
    (item) =>
      `<div class="py-4">` +
      `<h3 class="text-[16px] font-semibold text-ink">${escapeHtml(item.q)}</h3>` +
      `<p class="mt-1 text-[15px] text-ink-2 leading-relaxed">${escapeHtml(item.a)}</p>` +
      `</div>`
  ).join('');
  return (
    `<div class="min-h-[100dvh] bg-cream text-ink flex flex-col">` +
    `<header class="flex items-center px-5 pt-2 pb-0 sm:px-8 sm:pt-7 sm:pb-2">${backLink('cream', true, '/blog')}</header>` +
    `<main class="flex-1 w-full max-w-2xl mx-auto px-6 sm:px-8 pt-2 sm:pt-8 pb-16">` +
    `<h1 class="text-[26px] sm:text-[40px] leading-[1.12] tracking-[-0.02em] font-semibold text-ink">${escapeHtml(l.h1)}</h1>` +
    `<p class="mt-2.5 sm:mt-6 text-[15px] text-ink-2 leading-[1.45] sm:leading-relaxed font-semibold">${escapeHtml(LANDING_LEAD)}</p>` +
    `<p class="mt-2 sm:mt-4 text-[15px] text-ink-2 leading-[1.45] sm:leading-relaxed">${escapeHtml(l.body)}</p>` +
    `<ol class="mt-2.5 sm:mt-8 space-y-1 sm:space-y-3 text-[15px] text-ink-2 leading-[1.45] sm:leading-relaxed">${steps}</ol>` +
    `<a href="/" class="mt-3 sm:mt-8 inline-flex items-center justify-center gap-2 rounded-full font-medium bg-ink text-white px-6 py-3 sm:py-4 text-base w-full max-w-sm">Start a game <span aria-hidden="true">→</span></a>` +
    `<h2 class="mt-8 text-[17px] font-semibold text-ink">FAQ</h2>` +
    `<div class="divide-y divide-line-2">${faqs}</div>` +
    relatedLinks(l.path) +
    interlinks('cream', l.path) +
    `</main></div>`
  );
}

function landingJsonLd(l) {
  const url = `${SITE}${l.path}`;
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: l.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: `How to use Chipless: ${l.h1}`,
      step: LANDING_STEPS.map((text, i) => ({ '@type': 'HowToStep', position: i + 1, text })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Chipless',
      url: `${SITE}/`,
      applicationCategory: 'GameApplication',
      operatingSystem: 'Web',
      description: `${LANDING_LEAD} ${l.body}`,
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
      mainEntityOfPage: url,
    },
  ];
}

function pageJsonLd(page) {
  if (page.id === 'howTo') return [articleJsonLd()];
  if (page.landing) return landingJsonLd(page.landing);
  return [];
}

function renderPageBody(id) {
  if (PAGES[id] && PAGES[id].landing) return renderLandingBody(PAGES[id].landing);
  if (id === 'howTo') return renderHowToBody();
  if (id === 'faq') return renderFaqBody();
  if (id === 'about') return renderAboutBody();
  if (id === 'blog') return renderBlogBody();
  throw new Error(`Unknown SEO page: ${id}`);
}

function articleJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'How to play poker without chips',
    description: PAGES.howTo.description,
    author: {
      '@type': 'Person',
      name: 'Michael Jonna',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Chipless',
    },
    mainEntityOfPage: PAGES.howTo.canonical,
    url: PAGES.howTo.canonical,
  };
}

/** FAQPage schema with every Q&A shown on /faq (lead question first). */
function faqJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    url: PAGES.faq.canonical,
    mainEntity: [FAQ_LEAD, ...FAQS].map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };
}

module.exports = {
  RELATED,
  relatedLinks,
  SITE,
  FAQS,
  FAQ_LEAD,
  FAQ_SUBLINE,
  ABOUT_SECTIONS,
  HOW_TO_STEPS,
  HOW_TO_HOOK,
  HOW_TO_BEFORE_STEPS,
  POSTS,
  PAGES,
  renderPageBody,
  LANDINGS,
  LANDING_LEAD,
  LANDING_STEPS,
  pageJsonLd,
  renderBlogBody,
  articleJsonLd,
  faqJsonLd,
};
